import { setLoggedInUserInfo, submitUserLoggedInDate } from "components/app/App.slice";
import jwtDecode from "jwt-decode";
import { uniq } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { AppDispatch } from "redux/store";
import { axiosPostToken, getServiceType, instance } from "utils/APIHelper";
import { clearStorages, handleUnauthorizedAccess } from "utils/AuthUtils";
import { APACregion, dummyInnCodes, missingIncodeText } from "utils/CommonConstants";
import { decrypt, encrypt, getIsCorpUser } from "utils/CommonFunctions";
import { v4 as uuidv4 } from "uuid";
import { TOKEN_URL } from "../../../utils/URLConstants";
import RegionInterceptPage from "./RegionInterceptPage";

interface Props {
  isLoggedIn?: boolean;
  setIsLoggedIn?: any;
}

function Login(props: Props) {
  const { setIsLoggedIn } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [restrictRegionBanner, setRestrictRegionBanner] = useState(false);
  const [allowedRegionBanner, setAllowedRegionBanner] = useState(false);
  const isAPACEnabled = process.env.REACT_APP_APAC_REGION_ENABLED === "true";

  const redirectToLogin = useCallback(() => {
    setIsLoggedIn(false);
    handleUnauthorizedAccess();
  }, [setIsLoggedIn, location.pathname]);

  const handleButtonClick = (redirectTo: string) => {
    if (redirectTo === "Boost") {
      redirectToBoost();
    } else {
      setAllowedRegionBanner(false);
      redirectToGen();
    }
  };

  const redirectToGen = () => {
    clearStorages();
    setRestrictRegionBanner(true);
    setTimeout(() => {
      window.location.replace(process.env.REACT_APP_GEN_LOGIN_URL ? process.env.REACT_APP_GEN_LOGIN_URL : "");
    }, 10000);
  };

  const redirectToBoost = () => {
    const decoded = jwtDecode(localStorage.getItem("id_token") ?? "");
    setDataInLocalStorage(decoded);
    setIsLoggedIn(true);
    const url = localStorage.getItem("url");
    if (url) navigate(url);
  };

  const getDefaultInncode = (data: any) => {
    if (data["inncode"] && !dummyInnCodes.includes(String(data["inncode"]).toLowerCase())) return data["inncode"];
    else if (data["innCodes"]?.length > 0)
      return data["innCodes"]?.filter((item: any) => !dummyInnCodes.includes(String(item).toLowerCase())).sort()[0];
    else return missingIncodeText;
  };

  const getInnCodeList = (list = [], inncode: string) => {
    let resultList: any;
    let combinedList = inncode ? uniq([...list, inncode]) : list;
    if (combinedList.length > 0) {
      resultList = combinedList.filter((item: string) => !dummyInnCodes.includes(String(item).toLowerCase()));
    } else {
      resultList = missingIncodeText;
    }
    return resultList;
  };

  const setDataInLocalStorage = (data: any) => {
    secureLocalStorage.setItem("un", data["displayName"]);
    secureLocalStorage.setItem("email", data["mail"]);
    secureLocalStorage.setItem("designation", data["hltPositionDescr"]);
    secureLocalStorage.setItem("hltOrganization", data["hltOrganization"]);
    secureLocalStorage.setItem("firstName", data["givenName"]);
    secureLocalStorage.setItem("lastName", data["sn"]);
    secureLocalStorage.setItem("displayName", data["displayName"]);
    localStorage.setItem("hltInnCodes", getInnCodeList(data["innCodes"], data["inncode"]));
    localStorage.setItem("defaultInncode", getDefaultInncode(data));
    localStorage.setItem("expired_in", data["exp"]);
    localStorage.setItem("hltId", data["hltId"]);
  };

  const checkAndAddAdditionalAssociate = async (decoded: any) => {
    const params = {};
    await instance
      .get(`${getServiceType("user")}associates?size=1&hltIds=${decoded["hltId"]}`, {
        params
      })
      .then(async (response: any) => {
        if (Object.keys(response?.data?.users).length === 0) {
          const inncodeList = getInnCodeList(decoded["innCodes"], decoded["inncode"]);
          const userInfo = {
            hltId: decoded["hltId"],
            displayName: decoded["displayName"],
            firstName: decoded["givenName"],
            lastName: decoded["sn"],
            email: decoded["mail"],
            designation: decoded["hltPositionDescr"],
            innCodes: inncodeList === missingIncodeText ? [] : inncodeList,
            lobbyUserType: decoded["lobbyUserType"],
            countryCode: decoded["countryCode"]
          };
          await instance.post(`${getServiceType("user")}additional-associates`, userInfo);
        } else {
          dispatch(setLoggedInUserInfo(response?.data?.users));
        }
      })
      .catch((error) => {
        console.log("Api error", error);
      });
  };

  const handleRegionLogic = () => {
    const isCorpUser = getIsCorpUser(decrypt("salt", localStorage.getItem("lobbyUserType")));
    const countryCode = (localStorage.getItem("country_code") || "").toUpperCase();

    if (isCorpUser) {
      isAPACEnabled ? redirectToBoost() : setAllowedRegionBanner(true);
    } else {
      if (isAPACEnabled || !APACregion.includes(countryCode)) {
        redirectToBoost();
      } else {
        redirectToGen();
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("id_token");
    const code = window.location.search.split("code=")[1];
    if (code) {
      if (!token) {
        axiosPostToken(TOKEN_URL, code)
          .then(async (response: any) => {
            const decoded: any = jwtDecode(response.data.access_token);
            localStorage.setItem("X-ConsumerID", uuidv4());
            localStorage.setItem("refresh_token", response.data.refresh_token);
            localStorage.setItem("id_token", response.data.access_token);
            localStorage.setItem("country_code", decoded["countryCode"]);
            localStorage.setItem("lobbyUserType", encrypt("salt", decoded["lobbyUserType"]));
            dispatch(submitUserLoggedInDate({}));
            await checkAndAddAdditionalAssociate(decoded);
            //logic for redirection for allowed and non allowed users
            handleRegionLogic();
          })
          .catch((error) => {
            console.log("Login error", error);
            handleUnauthorizedAccess(true);
          });
      } else {
        handleRegionLogic();
      }
    } else if (token) handleRegionLogic();
    else redirectToLogin();
  }, [navigate, redirectToLogin]);

  return (
    <>
      {restrictRegionBanner && (
        <RegionInterceptPage
          type="restrictRegions"
          title="Welcome!"
          description="Redirecting to GEN. You may need to re-enter your login to access the platform"
        />
      )}
      {allowedRegionBanner && (
        <RegionInterceptPage
          type="allowedRegions"
          title="Welcome!"
          description="Select the Hilton Action Planning Tool you would like to access."
          handleOnClick={handleButtonClick}
        />
      )}
    </>
  );
}

export default Login;
